import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, lastValueFrom } from 'rxjs';

export interface Stats {
  showStats: boolean;
  wellbeingIntervals: WellbeingInterval[];
  wellbeingStats: WellbeingStats[];
}

export interface WellbeingInterval {
  color: number;
  end: number;
  maxScore: number;
  minScore: number;
  start: number;
}

export interface WellbeingStats {
  id: number;
  totalScore: number;
  utcDate: Date;
  interventionText: string;
  interventionColor: number;
  note: string | null;
  noteUtcDate: string | null;
  individualScores: IndividualScores;
}

export interface IndividualScores {
  moodScore: number;
  relaxedScore: number;
  energyScore: number;
  sleepScore: number;
  motivationScore: number;
}

export interface TeamStats {
  name: string;
  howdyIndex: number | null;
  monthStart: string;
  participationThisMonth: number;
  metrics: Metric[];
  wellbeingIndexIntervals: WellbeingIndexInterval[];
  countryBenchmark: any; // TODO: add type
}

export interface Metric {
  id: number;
  departmentId: number;
  companyId: number | null;
  monthStart: string;
  months: number;
  categoryNoAnswer: number;
  categoryDarkGreen: number;
  categoryMediumGreen: number;
  categoryLightGreen: number;
  categoryYellow: number;
  categoryRed: number;
  participation: number;
  participationLast3Months: number;
  participationLastYear: number;
  avgScorePerAnswer: number | null;
  howdyIndex: number | null;
  createdOn: string;
}

export interface WellbeingIndexInterval {
  start: number;
  end: number;
  minScore: number;
  maxScore: number;
  color: number;
}

@Injectable({
  providedIn: 'root'
})
export class StatsService {
  private wellbeingStats: WellbeingStats[] = [];
  private statsSubject = new BehaviorSubject<Stats | null>(null);
  private teamStatsSubject = new BehaviorSubject<TeamStats[] | null>(null);
  public stats$ = this.statsSubject.asObservable();
  public teamStats$ = this.teamStatsSubject.asObservable();

  constructor(private http: HttpClient) {
    this.getStats();
    this.getTeamStats();
  }

  async getStats(): Promise<void> {
    try {
      const response = await lastValueFrom(this.http.get<Stats>('/api/Stats/GetStats'));

      if (response) {
        this.wellbeingStats = response.wellbeingStats.map(stat => ({
          ...stat,
          utcDate: new Date(stat.utcDate) as Date
        }));
        this.statsSubject.next({
          ...response,
          wellbeingStats: this.wellbeingStats
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }

  async getTeamStats(): Promise<void> {
    try {
      const response = await lastValueFrom(this.http.get<TeamStats[]>('/api/Stats/GetTeamStats'));

      if (response) {
        this.teamStatsSubject.next(response);
      }
    } catch (error) {
      console.error('Error fetching team stats:', error);
    }
  }
}
